class SiteHeaderNavigation extends HTMLElement {
  connectedCallback() {
    this.disclosures = Array.from(this.querySelectorAll('[data-header-disclosure]'));
    this.localizationForms = Array.from(this.querySelectorAll('localization-form'));
    this.overlay = this.querySelector('[data-header-overlay]');
    this.mobileViewport = window.matchMedia('(max-width: 989px)');
    this.hoverViewport = window.matchMedia('(hover: hover) and (pointer: fine)');
    this.onToggle = this.handleToggle.bind(this);
    this.onClick = this.handleClick.bind(this);
    this.onKeydown = this.handleKeydown.bind(this);
    this.onPointerOver = this.handlePointerOver.bind(this);
    this.onPointerOut = this.handlePointerOut.bind(this);
    this.onFocusIn = this.handleFocusIn.bind(this);
    this.onFocusOut = this.handleFocusOut.bind(this);
    this.onDocumentClick = this.handleDocumentClick.bind(this);
    this.onDocumentFocusIn = this.handleDocumentFocusIn.bind(this);
    this.onViewportChange = this.handleViewportChange.bind(this);
    this.onPageShow = this.handlePageShow.bind(this);
    this.onLocalizationToggle = this.handleLocalizationToggle.bind(this);
    this.discoveryCloseTimers = new Map();
    this.menuCloseTimers = new Map();
    this.cartBubble = document.querySelector('#cart-icon-bubble');
    this.cartBubbleObserver = this.cartBubble
      ? new MutationObserver(() => this.syncCartTriggers())
      : null;

    this.disclosures.forEach((disclosure) => disclosure.addEventListener('toggle', this.onToggle));
    this.disclosures.forEach((disclosure) => this.syncDisclosureAria(disclosure));
    this.addEventListener('click', this.onClick);
    this.addEventListener('pointerover', this.onPointerOver);
    this.addEventListener('pointerout', this.onPointerOut);
    this.addEventListener('focusin', this.onFocusIn);
    this.addEventListener('focusout', this.onFocusOut);
    this.addEventListener('localization:toggle', this.onLocalizationToggle);
    document.addEventListener('keydown', this.onKeydown);
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('focusin', this.onDocumentFocusIn);
    this.mobileViewport.addEventListener('change', this.onViewportChange);
    window.addEventListener('pageshow', this.onPageShow);
    this.cartBubbleObserver?.observe(this.cartBubble, { childList: true, subtree: true });
    this.syncCartTriggers();
  }

  disconnectedCallback() {
    this.disclosures.forEach((disclosure) => disclosure.removeEventListener('toggle', this.onToggle));
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('pointerover', this.onPointerOver);
    this.removeEventListener('pointerout', this.onPointerOut);
    this.removeEventListener('focusin', this.onFocusIn);
    this.removeEventListener('focusout', this.onFocusOut);
    this.removeEventListener('localization:toggle', this.onLocalizationToggle);
    document.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('focusin', this.onDocumentFocusIn);
    this.mobileViewport.removeEventListener('change', this.onViewportChange);
    window.removeEventListener('pageshow', this.onPageShow);
    this.cartBubbleObserver?.disconnect();
    this.discoveryCloseTimers.forEach((timer) => window.clearTimeout(timer));
    this.discoveryCloseTimers.clear();
    this.menuCloseTimers.forEach((timer) => window.clearTimeout(timer));
    this.menuCloseTimers.clear();
    this.unlockViewport();
  }

  syncCartTriggers() {
    if (!this.cartBubble) return;
    document.querySelectorAll('[data-cart-trigger]').forEach((trigger) => {
      if (trigger === this.cartBubble || trigger.isEqualNode(this.cartBubble)) return;
      trigger.replaceChildren(...Array.from(this.cartBubble.childNodes, (node) => node.cloneNode(true)));

      if (!trigger.matches('.site-header__cart-trigger--mobile-header')) return;

      const bubble = trigger.querySelector('.cart-count-bubble');
      const count = Number.parseInt(bubble?.dataset.cartCount || '0', 10);
      if (!bubble || count < 1) return;

      let visibleCount = bubble.querySelector('[aria-hidden="true"]');
      if (!visibleCount) {
        visibleCount = document.createElement('span');
        visibleCount.setAttribute('aria-hidden', 'true');
        bubble.append(visibleCount);
      }
      visibleCount.textContent = count > 9 ? '9+' : String(count);
    });
  }

  handleToggle(event) {
    const disclosure = event.currentTarget;
    this.syncDisclosureAria(disclosure);

    if (disclosure.open) {
      this.closeDisclosuresExcept(disclosure);
      this.closeLocalizationForms(disclosure.querySelector('localization-form'));

      const type = disclosure.dataset.headerDisclosure;
      if (type === 'discovery') {
        requestAnimationFrame(() => this.positionDiscoveryPanel(disclosure.closest('[data-discovery-hover]')));
      }
      if (type === 'desktop-menu') {
        const activeCatalogueTab = disclosure.querySelector('[data-header-catalogue-tab][aria-selected="true"]');
        if (activeCatalogueTab) requestAnimationFrame(() => this.activateCatalogueTab(activeCatalogueTab));
      }
      if (this.mobileViewport.matches && (type === 'explore' || type === 'account' || type === 'menu' || type === 'context')) {
        requestAnimationFrame(() =>
          disclosure.querySelector('.site-header__mobile-panel, .site-header__context-popover')?.focus(),
        );
      }
    } else {
      disclosure.querySelectorAll('localization-form').forEach((form) => form.hidePanel?.());
      this.resetExplore(disclosure);
    }

    this.syncDocumentState();
  }

  handleClick(event) {
    const mobileAction = event.target.closest('.site-header__mobile-nav .site-header__mobile-action');
    if (
      mobileAction &&
      this.mobileViewport.matches &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches &&
      'vibrate' in navigator
    ) {
      navigator.vibrate(8);
    }

    const navigationLink = event.target.closest('a[href]');
    const owningDisclosure = navigationLink?.closest('[data-header-disclosure]');
    if (owningDisclosure) this.closeDisclosure(owningDisclosure, false);

    const closeButton = event.target.closest('[data-header-close]');
    if (closeButton) {
      this.closeDisclosure(closeButton.closest('details'), true);
      return;
    }

    const catalogueTab = event.target.closest('[data-header-catalogue-tab]');
    if (catalogueTab) {
      this.activateCatalogueTab(catalogueTab);
      return;
    }

    const exploreOpen = event.target.closest('[data-header-explore-open]');
    if (exploreOpen) {
      this.openExploreLevel(exploreOpen);
      return;
    }

    const exploreBack = event.target.closest('[data-header-explore-back]');
    if (exploreBack) {
      this.showExploreParent(exploreBack);
      return;
    }

    if (event.target === this.overlay) this.closeAll(true);
  }

  handlePointerOver(event) {
    if (this.mobileViewport.matches || !this.hoverViewport.matches || event.pointerType === 'touch') return;
    if (this.localizationForms.some((form) => form.elements?.button?.getAttribute('aria-expanded') === 'true')) return;

    const catalogueTab = event.target.closest('[data-header-catalogue-tab]');
    if (catalogueTab) this.activateCatalogueTab(catalogueTab);

    const listenTrigger = event.target.closest('[data-header-listen-trigger]');
    if (listenTrigger) this.activateListenPreview(listenTrigger);

    const menuItem = event.target.closest('[data-header-menu-hover]');
    if (menuItem && !menuItem.contains(event.relatedTarget)) this.openHeaderMenu(menuItem);

    const item = event.target.closest('[data-discovery-hover]');
    if (!item || item.contains(event.relatedTarget)) return;
    this.openDiscoveryItem(item);
  }

  handlePointerOut(event) {
    if (this.mobileViewport.matches || !this.hoverViewport.matches || event.pointerType === 'touch') return;

    const menuItem = event.target.closest('[data-header-menu-hover]');
    if (menuItem && !menuItem.contains(event.relatedTarget)) this.scheduleHeaderMenuClose(menuItem);

    const item = event.target.closest('[data-discovery-hover]');
    if (!item || item.contains(event.relatedTarget)) return;
    this.scheduleDiscoveryClose(item);
  }

  handleFocusIn(event) {
    if (this.mobileViewport.matches || !event.target.matches(':focus-visible')) return;

    const catalogueTab = event.target.closest('[data-header-catalogue-tab]');
    if (catalogueTab) this.activateCatalogueTab(catalogueTab);

    const listenTrigger = event.target.closest('[data-header-listen-trigger]');
    if (listenTrigger) this.activateListenPreview(listenTrigger);

    const menuItem = event.target.closest('[data-header-menu-hover]');
    if (menuItem) this.openHeaderMenu(menuItem);

    const item = event.target.closest('[data-discovery-hover]');
    if (item) this.openDiscoveryItem(item);
  }

  handleFocusOut(event) {
    if (this.mobileViewport.matches) return;

    const menuItem = event.target.closest('[data-header-menu-hover]');
    if (menuItem && !menuItem.contains(event.relatedTarget)) this.scheduleHeaderMenuClose(menuItem);

    const item = event.target.closest('[data-discovery-hover]');
    if (!item || item.contains(event.relatedTarget)) return;
    this.scheduleDiscoveryClose(item);
  }

  handleDocumentClick(event) {
    if (this.mobileViewport.matches) return;

    const openDisclosure = this.getOpenDisclosure();
    if (openDisclosure && !openDisclosure.contains(event.target)) {
      this.closeDisclosure(openDisclosure, false);
    }
  }

  handleDocumentFocusIn(event) {
    if (this.mobileViewport.matches) return;

    const openDisclosure = this.getOpenDisclosure();
    if (openDisclosure?.closest('details-modal')) return;
    if (openDisclosure && !openDisclosure.contains(event.target)) {
      this.closeDisclosure(openDisclosure, false);
    }
  }

  openDiscoveryItem(item) {
    const disclosure = item.querySelector('[data-header-disclosure="discovery"]');
    if (!disclosure) return;

    this.clearDiscoveryClose(item);
    this.closeDisclosuresExcept(disclosure);
    this.closeLocalizationForms();
    disclosure.open = true;
  }

  openHeaderMenu(item) {
    const disclosure = item.querySelector('[data-header-disclosure="desktop-menu"]');
    if (!disclosure) return;

    this.clearHeaderMenuClose(item);
    this.closeDisclosuresExcept(disclosure);
    this.closeLocalizationForms();
    disclosure.open = true;
  }

  scheduleHeaderMenuClose(item) {
    this.clearHeaderMenuClose(item);
    const timer = window.setTimeout(() => {
      if (item.matches(':hover') || item.contains(document.activeElement)) return;
      const disclosure = item.querySelector('[data-header-disclosure="desktop-menu"]');
      if (disclosure) this.closeDisclosure(disclosure, false);
      this.menuCloseTimers.delete(item);
    }, 180);
    this.menuCloseTimers.set(item, timer);
  }

  clearHeaderMenuClose(item) {
    const timer = this.menuCloseTimers.get(item);
    if (timer === undefined) return;
    window.clearTimeout(timer);
    this.menuCloseTimers.delete(item);
  }

  activateListenPreview(trigger) {
    const root = trigger.closest('[data-header-listen-preview]');
    const key = trigger.dataset.headerListenTrigger;
    if (!root || !key) return;

    root.dataset.activeListenPreview = key;
    root.querySelectorAll('[data-header-listen-trigger]').forEach((link) => {
      link.classList.toggle('is-preview-active', link === trigger);
    });
    root.querySelectorAll('[data-header-listen-panel]').forEach((panel) => {
      panel.hidden = panel.dataset.headerListenPanel !== key;
    });
  }

  scheduleDiscoveryClose(item) {
    this.clearDiscoveryClose(item);
    const timer = window.setTimeout(() => {
      if (item.matches(':hover') || item.contains(document.activeElement)) return;
      const disclosure = item.querySelector('[data-header-disclosure="discovery"]');
      if (disclosure) this.closeDisclosure(disclosure, false);
      this.discoveryCloseTimers.delete(item);
    }, 120);
    this.discoveryCloseTimers.set(item, timer);
  }

  clearDiscoveryClose(item) {
    const timer = this.discoveryCloseTimers.get(item);
    if (timer === undefined) return;
    window.clearTimeout(timer);
    this.discoveryCloseTimers.delete(item);
  }

  positionDiscoveryPanel(item) {
    const panel = item?.querySelector('[data-discovery-panel]');
    if (!panel) return;

    panel.style.removeProperty('--trade-hnv2-discovery-panel-shift');
    const rect = panel.getBoundingClientRect();
    const viewportGutter = 16;
    let shift = 0;

    if (rect.right > window.innerWidth - viewportGutter) {
      shift = window.innerWidth - viewportGutter - rect.right;
    }
    if (rect.left + shift < viewportGutter) {
      shift += viewportGutter - (rect.left + shift);
    }

    if (shift !== 0) panel.style.setProperty('--trade-hnv2-discovery-panel-shift', `${shift}px`);
  }

  handleLocalizationToggle(event) {
    if (!event.detail?.open) return;

    const ownerDisclosure = event.target.closest('[data-header-disclosure]');
    this.closeDisclosuresExcept(ownerDisclosure);
    this.closeLocalizationForms(event.target);
    this.syncDocumentState();
  }

  handleKeydown(event) {
    const catalogueTab = event.target.closest('[data-header-catalogue-tab]');
    if (catalogueTab && ['ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) {
      const tabs = Array.from(catalogueTab.closest('[role="tablist"]')?.querySelectorAll('[data-header-catalogue-tab]') || []);
      if (tabs.length > 0) {
        event.preventDefault();
        const currentIndex = tabs.indexOf(catalogueTab);
        let nextIndex = currentIndex;
        if (event.key === 'ArrowUp') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
        if (event.key === 'ArrowDown') nextIndex = (currentIndex + 1) % tabs.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = tabs.length - 1;
        this.activateCatalogueTab(tabs[nextIndex]);
        tabs[nextIndex].focus();
      }
      return;
    }

    if (event.key === 'Escape') {
      const openDisclosure = this.disclosures.find((disclosure) => disclosure.open);
      if (openDisclosure) {
        event.preventDefault();
        this.closeDisclosure(openDisclosure, true);
      }
      return;
    }

    const openModal = this.getOpenMobileDisclosure();
    if (!openModal) return;

    if (event.key !== 'Tab') return;

    const panel = openModal.querySelector('.site-header__mobile-panel, .site-header__context-popover');
    if (!panel) return;
    const focusable = Array.from(
      panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'),
    ).filter((element) => !element.closest('[hidden]'));

    if (focusable.length === 0) {
      event.preventDefault();
      panel.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const focusIsOutsideChildren = document.activeElement === panel || !panel.contains(document.activeElement);
    if (event.shiftKey && (document.activeElement === first || focusIsOutsideChildren)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (document.activeElement === last || focusIsOutsideChildren)) {
      event.preventDefault();
      first.focus();
    }
  }

  handleViewportChange() {
    this.closeAll(false);
  }

  handlePageShow() {
    this.closeAll(false);
  }

  activateCatalogueTab(tab) {
    const catalogue = tab?.closest('[data-header-catalogue]');
    const targetKey = tab?.dataset.headerCatalogueTab;
    if (!catalogue || !targetKey) return;

    catalogue.querySelectorAll('[data-header-catalogue-tab]').forEach((candidate) => {
      const isActive = candidate === tab;
      candidate.classList.toggle('is-active', isActive);
      candidate.setAttribute('aria-selected', isActive ? 'true' : 'false');
      candidate.tabIndex = isActive ? 0 : -1;
    });

    catalogue.querySelectorAll('[data-header-catalogue-panel]').forEach((panel) => {
      const isActive = panel.dataset.headerCataloguePanel === targetKey;
      panel.classList.toggle('is-active', isActive);
      panel.hidden = !isActive;
      if (isActive) panel.querySelectorAll('hnv2-media-status').forEach((media) => media.prepare?.());
    });
  }

  getOpenMobileDisclosure() {
    return this.disclosures.find((disclosure) => {
      const type = disclosure.dataset.headerDisclosure;
      return disclosure.open && (type === 'explore' || type === 'account' || type === 'menu' || type === 'context');
    });
  }

  getOpenDisclosure() {
    return this.disclosures.find((disclosure) => disclosure.open);
  }

  openExploreLevel(trigger) {
    const disclosure = trigger.closest('[data-header-disclosure]');
    const root = disclosure?.querySelector('[data-header-explore-root]');
    const level = disclosure?.querySelector(`[data-header-explore-level="${trigger.dataset.headerExploreOpen}"]`);
    if (!root || !level) return;

    if (root.contains(trigger)) {
      this.exploreRootTriggers ||= new WeakMap();
      this.exploreRootTriggers.set(disclosure, trigger);
    }
    root.hidden = true;
    disclosure.querySelectorAll('[data-header-explore-level]').forEach((candidate) => {
      candidate.hidden = candidate !== level;
    });
    level.hidden = false;
    level.querySelector('[data-header-explore-back]')?.focus();
  }

  showExploreParent(trigger) {
    const disclosure = trigger.closest('[data-header-disclosure]');
    const parentKey = trigger.dataset.headerExploreBack;
    if (parentKey === 'root') {
      this.showExploreRoot(disclosure, true);
      return;
    }

    const parent = disclosure?.querySelector(`[data-header-explore-level="${parentKey}"]`);
    if (!parent) return;

    disclosure.querySelector('[data-header-explore-root]').hidden = true;
    disclosure.querySelectorAll('[data-header-explore-level]').forEach((level) => {
      level.hidden = level !== parent;
    });
    parent.hidden = false;
    parent.querySelector(`[data-header-explore-open="${trigger.closest('[data-header-explore-level]')?.dataset.headerExploreLevel}"]`)?.focus();
  }

  showExploreRoot(disclosure, restoreFocus) {
    const root = disclosure?.querySelector('[data-header-explore-root]');
    if (!root) return;

    root.hidden = false;
    disclosure.querySelectorAll('[data-header-explore-level]').forEach((level) => {
      level.hidden = true;
    });
    if (restoreFocus) {
      const opener = this.exploreRootTriggers?.get(disclosure);
      (opener && root.contains(opener) ? opener : root.querySelector('a, button'))?.focus();
    }
  }

  resetExplore(disclosure) {
    if (disclosure?.dataset.headerDisclosure === 'explore') {
      this.showExploreRoot(disclosure, false);
      this.exploreRootTriggers?.delete(disclosure);
    }
  }

  closeLocalizationForms(activeForm = null) {
    this.localizationForms.forEach((form) => {
      if (form === activeForm) return;
      form.hidePanel?.();
    });
  }

  syncDisclosureAria(disclosure) {
    disclosure?.querySelector(':scope > summary')?.setAttribute('aria-expanded', disclosure.open ? 'true' : 'false');
  }

  closeDisclosuresExcept(activeDisclosure = null) {
    this.disclosures.forEach((disclosure) => {
      if (disclosure !== activeDisclosure) this.closeDisclosure(disclosure, false);
    });
  }

  closeDisclosure(disclosure, restoreFocus) {
    if (!disclosure?.open) return;
    const trigger = disclosure.querySelector(':scope > summary');
    const modalOwner = disclosure.closest('details-modal');

    if (modalOwner?.detailsContainer === disclosure && typeof modalOwner.close === 'function') {
      modalOwner.close(restoreFocus);
    } else {
      disclosure.querySelectorAll('localization-form').forEach((form) => form.hidePanel?.());
      disclosure.open = false;
    }

    this.resetExplore(disclosure);
    this.syncDisclosureAria(disclosure);
    this.syncDocumentState();
    if (restoreFocus && !modalOwner) trigger?.focus();
  }

  closeAll(restoreFocus) {
    const openDisclosure = this.getOpenDisclosure();
    this.disclosures.forEach((disclosure) => this.closeDisclosure(disclosure, false));
    this.closeLocalizationForms();
    this.syncDocumentState();
    if (restoreFocus) openDisclosure?.querySelector(':scope > summary')?.focus();
  }

  lockViewport() {
    window.Hnv2ViewportLock.acquire(this);
  }

  unlockViewport() {
    window.Hnv2ViewportLock.release(this);
  }

  syncDocumentState() {
    if (this.mobileViewport.matches && this.getOpenMobileDisclosure()) {
      this.lockViewport();
    } else {
      this.unlockViewport();
    }
  }
}

class SiteStickyHeader extends HTMLElement {
  connectedCallback() {
    this.navigation = this.closest('header-navigation');
    this.desktopViewport = window.matchMedia('(min-width: 990px)');
    this.currentScrollTop = window.scrollY;
    this.headerBounds = { bottom: this.offsetHeight };
    this.onScroll = this.handleScroll.bind(this);
    this.onViewportChange = this.handleViewportChange.bind(this);
    this.resizeObserver = new ResizeObserver(() => this.handleResize());

    this.setHeaderHeight();
    this.createObserver();
    this.resizeObserver.observe(this);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.desktopViewport.addEventListener('change', this.onViewportChange);
    this.applyPersistentState();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    this.desktopViewport.removeEventListener('change', this.onViewportChange);
    this.observer?.disconnect();
    this.resizeObserver?.disconnect();
  }

  createObserver() {
    this.observer = new IntersectionObserver((entries) => {
      this.headerBounds = entries[0].intersectionRect;
      this.observer.disconnect();
    });
    this.observer.observe(this);
  }

  setHeaderHeight() {
    document.documentElement.style.setProperty('--header-height', `${this.offsetHeight}px`);
  }

  handleResize() {
    this.setHeaderHeight();
    this.headerBounds = { bottom: this.offsetHeight };
  }

  handleViewportChange() {
    this.reset();
    this.setHeaderHeight();
    this.applyPersistentState();
  }

  applyPersistentState() {
    if (this.desktopViewport.matches && this.dataset.stickyType === 'always') {
      this.classList.add('shopify-section-header-sticky');
    }
  }

  isStickyEnabled() {
    if (!this.desktopViewport.matches) return true;
    return this.dataset.stickyType !== 'none';
  }

  isAlwaysSticky() {
    return this.desktopViewport.matches && this.dataset.stickyType === 'always';
  }

  isInteractionOpen() {
    return Boolean(this.navigation?.querySelector('details[open]'));
  }

  handleScroll() {
    if (!this.isStickyEnabled() || this.isInteractionOpen()) return;

    const scrollTop = window.scrollY;
    const boundary = this.headerBounds.bottom || this.offsetHeight;

    if (scrollTop > this.currentScrollTop && scrollTop > boundary + 8) {
      this.hide();
    } else if (scrollTop < this.currentScrollTop && scrollTop > boundary) {
      this.reveal();
    } else if (scrollTop <= boundary) {
      this.reset();
      this.applyPersistentState();
    }

    this.currentScrollTop = scrollTop;
  }

  hide() {
    if (this.isAlwaysSticky()) return;
    this.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
    this.classList.remove('animate');
  }

  reveal() {
    if (this.isAlwaysSticky()) return;
    this.classList.add('shopify-section-header-sticky', 'animate');
    this.classList.remove('shopify-section-header-hidden');
  }

  reset() {
    this.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');
  }
}

if (!customElements.get('header-navigation')) customElements.define('header-navigation', SiteHeaderNavigation);
if (!customElements.get('site-sticky-header')) customElements.define('site-sticky-header', SiteStickyHeader);
